// ScheduleTable.jsx
import React from "react";
import PropTypes from "prop-types";
import styles from "./Scheduletable.module.css";

/**
 * Reusable Schedule Table Component
 *
 * @param {Array} periods             - [{ id, label, time }]
 * @param {Array} schedule            - [{ day, date, periods: [{ periodId, subject, section, status }] }]
 * @param {Boolean} showLegend
 * @param {Array} legendItems         - [{ key, label }]
 * @param {String} emptyPlaceholder
 * @param {Object} statusClassMap
 * @param {Boolean} selectable        - If true, clicking a cell with content will select/deselect it
 * @param {Array} selectedCells       - Array of { date, periodId } for currently-selected cells
 * @param {Function} onCellClick      - (cellInfo) => void — called on every cell click (with full cell info)
 */
const ScheduleTable = ({
  periods = [],
  schedule = [],
  showLegend = false,
  legendItems = [],
  emptyPlaceholder = "---",
  statusClassMap,
  selectable = false,
  selectedCells = [],
  onCellClick,
}) => {
  const defaultStatusClassMap = {
    submitted: styles.statusSubmitted,
    approved: styles.statusApproved,
    rejected: styles.statusRejected,
    resubmitted: styles.statusResubmitted,
    completed: styles.statusCompleted,
    highlight: styles.statusHighlight,
  };

  const finalStatusMap = {
    ...defaultStatusClassMap,
    ...(statusClassMap || {}),
  };

  const getPeriodData = (dayPeriods, periodId) => {
    if (!dayPeriods) return null;
    const found = dayPeriods.find(
      (p) => String(p.periodId) === String(periodId),
    );
    if (!found) return null;
    return { ...found, status: found.status != null ? found.status : "" };
  };

  const getStatusClass = (status) => {
    if (status === null || status === undefined || status === "") return "";
    return finalStatusMap[status] || "";
  };

  const isCellSelected = (rowDate, periodId) =>
    selectedCells.some(
      (c) => c.date === rowDate && String(c.periodId) === String(periodId),
    );

  const handleCellClick = (row, period, cellData) => {
    if (!selectable) return;
    if (!cellData || !cellData.subject) return; // only selectable if cell has content
    if (typeof onCellClick === "function") {
      onCellClick({
        date: row.rawDate || row.date,
        day: row.day,
        periodId: period.id,
        subject: cellData.subject,
        section: cellData.section,
        status: cellData.status,
        rawPeriod: cellData,
      });
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.headerCellEmpty}></th>
              {periods.map((period) => (
                <th key={period.id} className={styles.headerCell}>
                  <span className={styles.periodLabel}>{period.label}</span>
                  {period.time && (
                    <span className={styles.periodTime}>{period.time}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {schedule.map((row, rowIndex) => (
              <tr key={`${row.day}-${rowIndex}`}>
                <td className={styles.dayCell}>
                  <span className={styles.dayName}>{row.day}</span>
                  {row.date && (
                    <span className={styles.dayDate}>({row.date})</span>
                  )}
                </td>

                {periods.map((period) => {
                  const cellData = getPeriodData(row.periods, period.id);
                  const hasContent = cellData && cellData.subject;
                  const statusClass = hasContent
                    ? getStatusClass(cellData.status)
                    : "";
                  const selected =
                    hasContent &&
                    isCellSelected(row.rawDate || row.date, period.id);
                  const clickableClass =
                    selectable && hasContent ? styles.clickableCell : "";
                  const selectedClass = selected ? styles.selectedCell : "";

                  return (
                    <td
                      key={`${row.day}-${period.id}`}
                      className={`${styles.bodyCell} ${statusClass} ${clickableClass} ${selectedClass}`}
                      onClick={() => handleCellClick(row, period, cellData)}
                    >
                      {hasContent ? (
                        <>
                          <span className={styles.subject}>
                            {cellData.subject}
                          </span>
                          {(cellData.section || cellData.secondary) && (
                            <span className={styles.secondary}>
                              {cellData.section || cellData.secondary}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className={styles.emptyCell}>
                          {emptyPlaceholder}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showLegend && legendItems && legendItems.length > 0 && (
        <div className={styles.legend}>
          {legendItems.map((item) => (
            <span
              key={item.key}
              className={`${styles.legendItem} ${getStatusClass(item.key)}`}
            >
              {item.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

ScheduleTable.propTypes = {
  periods: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      time: PropTypes.string,
    }),
  ).isRequired,
  schedule: PropTypes.arrayOf(
    PropTypes.shape({
      day: PropTypes.string.isRequired,
      date: PropTypes.string,
      rawDate: PropTypes.string,
      periods: PropTypes.arrayOf(
        PropTypes.shape({
          periodId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
            .isRequired,
          subject: PropTypes.string,
          section: PropTypes.string,
          secondary: PropTypes.string,
          status: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
      ).isRequired,
    }),
  ).isRequired,
  showLegend: PropTypes.bool,
  legendItems: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  emptyPlaceholder: PropTypes.string,
  statusClassMap: PropTypes.object,
  selectable: PropTypes.bool,
  selectedCells: PropTypes.array,
  onCellClick: PropTypes.func,
};

export default ScheduleTable;
